<template>
  <HSelect
    id="channel-dropdown"
    :options="channels"
    option-key="id"
    option-label="name"
    :value="channel"
    :label="label"
    :readonly="disableTyping"
    :no-border="disableTyping"
    :required="required"
    :disabled="disabled"
    :placeholder="placeholder"
    :maxlength="maxlength"
    @update:value="handleSelect"
    @input="handleInput"
    @enter="handleEnter"
    @focusout="handleFocusout"
    @toggle="handleToggle"
  >
    <template v-if="!disableTyping" #iconLeft>
      <WIcon icon="icSearch" />
    </template>

    <template #options="{ option }">
      <span v-if="option.isDivider" class="channel-select__divider">
        {{ option.name }} ({{ option.size }})
      </span>
      <div
        v-else
        class="channel-select__demand"
        :class="{ 'channel-select__new': option.isNew }"
      >
        {{ option.name }}

        <CDemandTag :demands="option.demands" class="channel-select__tag" />
      </div>
    </template>

    <template v-if="!disableTyping" #iconRight>
      <WIcon
        v-if="typing || loading"
        icon="icLoad"
        class="channel-select__loading"
      />
    </template>
  </HSelect>
</template>
<script>
import { WIcon } from '@alice-health/wonderland-vue'
import { mapGetters } from 'vuex'

import CDemandTag from '@/components/channels/atoms/CDemandTag.vue'
import { CHANNEL_KINDS, ChannelStatus, CHAT_TITLE } from '@/models/channels'

import { HSelect } from '../../atoms'

export default {
  components: { CDemandTag, WIcon, HSelect },
  props: {
    personId: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    newLabel: {
      type: String,
      default: 'Aperte enter para criar um novo canal'
    },
    value: {
      type: Object,
      default: null
    },
    channelId: {
      type: String,
      default: undefined
    },
    disableTyping: {
      type: Boolean,
      default: false
    },
    createTyping: {
      type: Boolean,
      default: false
    },
    disableCreate: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean
    },
    disabled: {
      type: Boolean
    },
    placeholder: {
      type: String,
      default: ''
    },
    showChat: {
      type: Boolean,
      default: false
    },
    showArchived: {
      type: Boolean,
      default: false
    },
    caseId: {
      type: String,
      default: undefined
    }
  },
  emits: ['input', 'toggle'],
  data() {
    return {
      channel: null,
      createDivider: {
        isNew: true,
        name: this.newLabel
      },
      activeDivider: { isDivider: true, name: 'Ativos', size: 0 },
      archivedDivider: { isDivider: true, name: 'Encerrados', size: 0 },
      recommendedDivider: {
        isDivider: true,
        name: 'Recomendados',
        size: 0
      },
      channels: [],
      typing: false,
      loading: false
    }
  },
  computed: {
    ...mapGetters('user', ['user']),
    ...mapGetters('pillars/channel/member', [
      'memberActiveChannels',
      'memberArchivedChannels'
    ]),
    hideCreateOption() {
      if (this.disableCreate) return true
      if (this.createTyping && !this.typing) return true
      return false
    },
    maxlength() {
      return this.createTyping ? 35 : undefined
    }
  },
  watch: {
    value(channelSelected) {
      this.selectChannel(channelSelected)
    },
    personId() {
      this.fetchChannels()
    }
  },
  mounted() {
    this.fetchChannels()
  },
  methods: {
    getChannelsGrouped(query) {
      let channels = []

      const activeChannels = this.memberActiveChannels

      //Recomended
      if (this.caseId) {
        const recommendedChannels = this.filterRecommended(
          this.memberActiveChannels
        )

        if (recommendedChannels && recommendedChannels.length) {
          channels = channels.concat(
            this.getChannels(
              recommendedChannels,
              this.recommendedDivider,
              query
            )
          )

          return channels
        }
      }

      //Create divider
      if (!this.hideCreateOption) {
        channels.push(this.createDivider)
      }

      //Active
      channels = channels.concat(
        this.getChannels(activeChannels, this.activeDivider, query)
      )

      //Archived
      if (this.showArchived) {
        channels = channels.concat(
          this.getChannels(
            this.memberArchivedChannels,
            this.archivedDivider,
            query
          )
        )
      }

      channels = channels.map(channel => ({
        ...channel,
        name: channel.name || CHAT_TITLE
      }))

      return channels
    },
    getChannels(currentChannels, divider, query) {
      let channels = []
      const filteredChannels = this.filter(currentChannels, query)
      if (filteredChannels.length > 0) {
        divider.size = filteredChannels.length
        channels.push(divider)
        channels = channels.concat(filteredChannels)
      }

      return channels
    },
    filterRecommended(activeChannels) {
      return activeChannels.filter(channel =>
        channel.demands?.find(demand => demand.caseId === this.caseId)
      )
    },
    filter(channelArray, query) {
      if (!this.showChat) {
        channelArray = channelArray.filter(
          channel => channel.kind !== CHANNEL_KINDS.CHAT
        )
      }
      if (!query) {
        return channelArray
      }
      const queryLower = query.toLowerCase()
      return channelArray.filter(channel =>
        channel?.name?.toLowerCase().includes(queryLower)
      )
    },
    handleSelect(channel) {
      this.channel = null
      this.typing = false
      if (channel.isDivider) {
        return
      }
      if (channel.id) {
        this.channel = channel
      }

      this.$emit('input', channel)
    },
    handleInput(value) {
      this.typing = true
      this.channels = this.getChannelsGrouped(value)
    },
    handleEnter(value) {
      if (this.disableCreate) {
        return
      }
      this.channel = {
        name: value
      }
      this.$emit('input', this.channel)
      this.typing = false
    },
    handleFocusout() {
      this.typing = false
      this.channels = this.getChannelsGrouped()
    },
    handleToggle(value) {
      if (value && this.personId) {
        this.$store.dispatch(
          'pillars/channel/member/fetchUpdatedActiveChannels',
          this.personId
        )
      }

      this.$emit('toggle', value)
    },
    selectChannel(channelSelected) {
      if (!channelSelected?.id) {
        return
      }
      this.channel = this.channels.find(
        channel => channelSelected.id === channel.id
      )
    },
    selectChannelByChannelId() {
      if (this.value?.id) {
        return
      }

      if (this.channelId) {
        const channel = this.memberActiveChannels.find(
          channel => channel.id === this.channelId
        )
        if (channel) this.handleSelect(channel)
      }
    },
    async fetchChannels() {
      this.loading = true
      if (this.personId) {
        await this.$store.dispatch(
          'pillars/channel/member/fetchMemberChannels',
          {
            personId: this.personId,
            status: [ChannelStatus.ACTIVE, ChannelStatus.ARCHIVED]
          }
        )
      }

      this.channels = this.getChannelsGrouped()
      this.selectChannel(this.value)
      this.selectChannelByChannelId()
      this.loading = false
    },
    isChat({ kind }) {
      return kind === CHANNEL_KINDS.CHAT
    }
  }
}
</script>
<style scoped lang="scss">
.channel-select {
  &__divider {
    letter-spacing: var(--gl-letter-spacing-rule-02);
    font-weight: var(--gl-font-weight-regular);
    font-size: var(--gl-font-size-xxxs);
    line-height: var(--gl-line-height-xxxs);
    color: var(--gl-color-shades-gray-60);
  }

  &__demand {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  &__new {
    color: var(--sys-color-content-active);
  }
  &__tag {
    margin-left: var(--gl-spacing-02);
    height: fit-content;
  }
}
</style>

<style lang="scss">
.channel-select__loading {
  animation: channel-select__animation-spin 1s linear infinite;
}

@keyframes channel-select__animation-spin {
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
</style>
